import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { CardCheckbox } from '@/components/ui/CardCheckbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CoverageType, ENABLED_COVERAGES, type EnabledCoverage } from '@/models/insuranceCoverage'
import { updateCoverageNeeds } from '@/models/user'
import { COVERAGE_ICONS } from './coverageIcons'
import { useRequoteOnSave } from './requoteOnSave'

interface ChangeCoveragesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
  /** Coverage types currently on the application. */
  selected: string[]
  /** Refetches the application so the summary picks the change up. */
  onChanged: () => void
  /** The coverages this business can choose; trucking's when the application doesn't say. */
  menu?: ReadonlyArray<EnabledCoverage>
}

/**
 * Picks what the customer wants covered. Saving records the choice and nothing else —
 * asking the market again is a re-quote, which only Cai runs.
 */
export function ChangeCoveragesDialog({
  open,
  onOpenChange,
  orgId,
  selected,
  onChanged,
  menu = ENABLED_COVERAGES,
}: ChangeCoveragesDialogProps) {
  const [picked, setPicked] = useState<string[]>(selected)
  const wasOpen = useRef(false)
  const [isSaving, setIsSaving] = useState(false)
  const requote = useRequoteOnSave()

  // The dialog stays mounted across opens, so re-seed from what's on file — but only on
  // the transition into open. The page polls the application every 4s, which hands down
  // a fresh `selected` array each time; re-seeding on that would undo whatever the
  // customer had just ticked, a second or two after they ticked it.
  useEffect(() => {
    // Required coverages render disabled, so they have to start ticked.
    if (open && !wasOpen.current) {
      const required = menu.filter((option) => option.required).map((option) => option.coverageType)
      setPicked([...new Set([...required, ...selected])])
    }
    wasOpen.current = open
  }, [open, selected, menu])

  function toggle(type: CoverageType, checked: boolean) {
    setPicked((current) => (checked ? [...current, type] : current.filter((coverage) => coverage !== type)))
  }

  async function save() {
    setIsSaving(true)
    try {
      await updateCoverageNeeds(orgId, { selectedCoverages: picked as CoverageType[] })
      onOpenChange(false)
      onChanged()
      if (requote) await requote.run()
      else toast.success('Coverages updated')
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } }).response?.data?.error
      toast.error(message || "We couldn't update your coverages. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  const isUnchanged =
    picked.length === selected.length && picked.every((coverage) => selected.includes(coverage))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Change coverages</DialogTitle>
          <DialogDescription>
            The prices below are what the carriers quoted on your old selection, so they change once Cai has
            been back to the market.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 grid gap-2">
          {menu.map((option) => {
            const Icon = COVERAGE_ICONS[option.coverageType]
            return (
              <CardCheckbox
                key={option.coverageType}
                isChecked={picked.includes(option.coverageType)}
                disabled={option.required}
                className="py-4"
                contentClassName="items-start"
                onChange={(checked) => toggle(option.coverageType, checked)}
              >
                <span className="flex items-start gap-3">
                  <Icon className="mt-0.5 size-5 shrink-0" />
                  <span className="flex flex-col gap-1 text-left">
                    <span className="text-foreground font-bold">
                      {option.label}
                      {option.required && (
                        <span className="text-ink-400 ml-2 text-xs font-normal">Required</span>
                      )}
                    </span>
                    <span className="text-ink-400 text-sm font-normal">{option.description}</span>
                  </span>
                </span>
              </CardCheckbox>
            )
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={isUnchanged || picked.length === 0}
            loading={isSaving ? (requote?.pendingLabel ?? 'Saving…') : undefined}
          >
            {requote?.label ?? 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
